import { html, nothing } from "lit";
import { TAB_GROUPS, type Tab, titleForTab, iconForTab } from "../navigation.ts";
import type { UserRole } from "../storage.ts";
import {
  type TabVisibilityMap,
  EDITABLE_ROLES,
  dirtyKey,
} from "../controllers/tab-visibility.ts";
import {
  type RoleSettingsMap,
  ROLE_SETTINGS_EDITABLE_ROLES,
  CHAT_SHOW_PICKER_KEY,
  roleSettingsDirtyKey,
} from "../controllers/role-settings.ts";

export type UserSettingsProps = {
  loading: boolean;
  saving: boolean;
  visibilityMap: TabVisibilityMap;
  dirty: Set<string>;
  error: string | null;
  onToggle: (role: string, tabKey: string, visible: boolean) => void;
  onSave: () => void;
  onRefresh: () => void;
  roleSettingsMap: RoleSettingsMap;
  roleSettingsDirty: Set<string>;
  onRoleSettingToggle: (role: string, key: string, value: unknown) => void;
  onRoleSettingsSave: () => void;
};

const ALL_TABS: Tab[] = TAB_GROUPS.flatMap((g) => g.tabs as readonly Tab[]);

// Equal, fixed width for every role column so the checkboxes line up in a
// uniform grid regardless of header length ("Project Admin" vs "User").
const ROLE_COL_STYLE = "text-align: center; width: 116px; white-space: nowrap;";

const ROLE_LABELS: Record<UserRole, string> = {
  superadmin: "SuperAdmin",
  admin: "Admin",
  project_admin: "Project Admin",
  user: "User",
};

export function renderUserSettings(props: UserSettingsProps) {
  const allRoles: UserRole[] = ["superadmin", ...EDITABLE_ROLES];

  return html`
    <div class="card" style="overflow-x:auto">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div>
          <div class="card-title">User Settings</div>
          <div class="card-sub">Toggle sidebar tab visibility per role. SuperAdmin always sees all tabs.</div>
        </div>
        <div class="row" style="gap: 8px;">
          <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Loading…" : "Refresh"}
          </button>
          <button
            class="btn primary"
            ?disabled=${props.saving || props.dirty.size === 0}
            @click=${props.onSave}
          >
            ${props.saving ? "Saving…" : `Save${props.dirty.size > 0 ? ` (${props.dirty.size})` : ""}`}
          </button>
        </div>
      </div>

      ${props.error
        ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
        : nothing}

      <div class="callout info" style="margin-top: 12px;">
        Changes apply on next page reload for affected users.
      </div>

      <table class="settings-table" style="margin-top: 16px;">
        <thead>
          <tr>
            <th style="text-align: left; min-width: 220px;">Tab</th>
            ${allRoles.map((role) => html`<th style=${ROLE_COL_STYLE}>${ROLE_LABELS[role]}</th>`)}
          </tr>
        </thead>
        <tbody>
          ${ALL_TABS.map((tab) => {
            const title = titleForTab(tab);
            const iconName = iconForTab(tab);
            return html`
              <tr>
                <td style="display: flex; align-items: center; gap: 6px;">
                  <span class="config-nav__icon" style="width:14px;height:14px;flex-shrink:0;"></span>
                  <span>${title}</span>
                  <code class="muted" style="font-size:0.75em;">${tab}</code>
                </td>
                ${allRoles.map((role) => {
                  if (role === "superadmin") {
                    return html`<td style="text-align: center;">
                      <input type="checkbox" checked disabled title="SuperAdmin always sees all tabs" />
                    </td>`;
                  }
                  const isVisible = props.visibilityMap[role]?.[tab] ?? true;
                  const isDirty = props.dirty.has(dirtyKey(role, tab));
                  return html`<td style="text-align: center;">
                    <input
                      type="checkbox"
                      .checked=${isVisible}
                      ?data-dirty=${isDirty}
                      title=${isDirty ? "Unsaved change" : ""}
                      @change=${(e: Event) =>
                        props.onToggle(role, tab, (e.target as HTMLInputElement).checked)}
                    />
                  </td>`;
                })}
              </tr>
            `;
          })}
        </tbody>
      </table>

      <p class="muted" style="margin-top: 0.75rem;">
        ${ALL_TABS.length} tabs · ${EDITABLE_ROLES.length} configurable roles
        ${props.dirty.size > 0 ? html` · <strong>${props.dirty.size} unsaved changes</strong>` : nothing}
      </p>
    </div>

    <!-- Chat Header Settings -->
    <div class="card" style="margin-top: 16px;">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div>
          <div class="card-title">Chat Header Settings</div>
          <div class="card-sub">Per-role toggles for chat header UI elements.</div>
        </div>
        <button
          class="btn primary"
          ?disabled=${props.roleSettingsDirty.size === 0}
          @click=${props.onRoleSettingsSave}
        >
          Save${props.roleSettingsDirty.size > 0 ? ` (${props.roleSettingsDirty.size})` : ""}
        </button>
      </div>

      <table class="settings-table" style="margin-top: 16px;">
        <thead>
          <tr>
            <th style="text-align: left; min-width: 220px;">Setting</th>
            ${(["superadmin", ...ROLE_SETTINGS_EDITABLE_ROLES] as UserRole[]).map((role) =>
              html`<th style=${ROLE_COL_STYLE}>${ROLE_LABELS[role]}</th>`,
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Show project picker in chat header</td>
            ${(["superadmin", ...ROLE_SETTINGS_EDITABLE_ROLES] as UserRole[]).map((role) => {
              if (role === "superadmin") {
                return html`<td style="text-align: center;">
                  <input type="checkbox" checked disabled title="SuperAdmin always sees the picker" />
                </td>`;
              }
              const value = props.roleSettingsMap[role]?.[CHAT_SHOW_PICKER_KEY] ?? true;
              const isDirty = props.roleSettingsDirty.has(
                roleSettingsDirtyKey(role, CHAT_SHOW_PICKER_KEY),
              );
              return html`<td style="text-align: center;">
                <input
                  type="checkbox"
                  .checked=${value === true}
                  ?data-dirty=${isDirty}
                  @change=${(e: Event) =>
                    props.onRoleSettingToggle(
                      role,
                      CHAT_SHOW_PICKER_KEY,
                      (e.target as HTMLInputElement).checked,
                    )}
                />
              </td>`;
            })}
          </tr>
        </tbody>
      </table>
    </div>
  `;
}
